import { addDays, addHours, addMilliseconds, addMinutes, addMonths, addSeconds, addYears, differenceInDays, differenceInHours, differenceInMinutes, differenceInMonths, differenceInSeconds, differenceInYears, endOfDay, formatDistance, formatDistanceStrict, parseISO, roundToNearestMinutes, setHours, setMilliseconds, setMinutes, setSeconds, startOfDay, subDays, subHours, subMinutes, subMonths, subYears, } from 'date-fns';
import { DateTime, Interval } from 'luxon';
import { global, isDefined } from '../utils';
import { IsoDateFormat } from '../types';
import { getTimezoneAbbreviation } from './timezone';
//these consts are now deprecated, but keeping
//in case of legacy code, to prevent breaking app
export const timeFormatWorld = 'HH:mm';
export const timeFormatUs = 'h:mm a';
export const dateFormatWorld = 'dd.MM.yyyy';
export const dateFormatUs = 'MM.dd.yyyy';
export const dateTimeWorldFormat = 'DD.MM.yyyy HH:mm';
export const dateTimeUsFormat = 'MM.DD.yyyy h:mm a';
export const fullDateTimeWorldFormat = 'dd.MM.yyyy HH:mm:ss';
export const fullDateTimeUsFormat = 'MM.dd.yyyy h:mm:ss a';
export const datePickerWorldFormat = 'yyyy-MM-dd';
export const datePickerUsFormat = 'MM-dd-yyyy';
// END deprecated time/date consts
export const genericDateFormat = (date, formatString, timezone) => {
    if (isDefined(timezone)) {
        return DateTime.fromJSDate(date).setZone(timezone).toFormat(formatString);
    }
    return DateTime.fromJSDate(date).toFormat(formatString);
};
export const formatIanaTimeZone = (dateTime, localeFormat, ianaTimeZoneCode, showTimezoneAbbrev) => {
    const date = typeof dateTime === 'number' ? new Date(dateTime) : dateTime;
    return `${genericDateFormat(date, localeFormat, ianaTimeZoneCode)}${showTimezoneAbbrev ? ` ${getTimezoneAbbreviation(ianaTimeZoneCode)}` : ''}`;
};
export const convertDateFnsFormatToMomentFormat = (localeFormat) => {
    switch (localeFormat) {
        case datePickerWorldFormat:
            return 'YYYY-MM-DD';
        case datePickerUsFormat:
            return 'MM-DD-YYYY';
        case fullDateTimeWorldFormat:
            return 'DD.MM.YYYY HH:mm:ss';
        case fullDateTimeUsFormat:
            return 'MM.DD.YYYY h:mm:ss A';
        case dateTimeUsFormat:
            return 'MM.DD.YYYY h:mm A';
        case dateTimeWorldFormat:
            return 'DD.MM.YYYY HH:mm';
        case dateFormatUs:
            return 'MM.DD.YYYY';
        case dateFormatWorld:
            return 'DD.MM.YYYY';
        case timeFormatUs:
        case 'h:mm a':
            return 'h:mm A';
        case timeFormatWorld:
        case 'HH:mm':
            return 'HH:mm';
        case 'H:mm':
            return 'H:mm';
        case 'MMM dd':
            return 'MMM DD';
        case 'MM/dd/yyyy':
            return 'MM/DD/YYYY';
        case 'dd/MM/yyyy':
            return 'DD/MM/YYYY';
        case 'h:mm:ss a':
            return 'h:mm:ss A';
        case 'HH:mm:ss':
            return 'HH:mm:ss';
        case 'MM/dd/yyyy h:mm a':
            return 'MM/DD/YYYY h:mm A';
        case 'MM/dd/yyyy HH:mm':
            return 'MM/DD/YYYY HH:mm';
        case 'dd/MM/yyyy h:mm a':
            return 'DD/MM/YYYY h:mm A';
        case 'dd/MM/yyyy HH:mm':
            return 'DD/MM/YYYY HH:mm';
        case 'MM/dd/yyyy HH:mm:ss':
            return 'MM/DD/YYYY HH:mm:ss';
        case 'MM/dd/yyyy h:mm:ss a':
            return 'MM/DD/YYYY h:mm:ss A';
        case 'dd/MM/yyyy HH:mm:ss':
            return 'DD/MM/YYYY HH:mm:ss';
        case 'dd/MM/yyyy h:mm:ss a':
            return 'DD/MM/YYYY h:mm:ss A';
        default:
            return;
    }
};
export const determineTimeStringByTimezone = (time, localeFormat, ianaTimeZoneCode, showTimezone) => {
    if (ianaTimeZoneCode) {
        return formatIanaTimeZone(time, localeFormat, ianaTimeZoneCode, showTimezone);
    }
    else {
        return formatIanaTimeZone(time, localeFormat, 'local');
    }
};
export const getTimezoneDifferenceInMs = (ianaTimeZone) => {
    if (!isDefined(ianaTimeZone))
        return 0;
    const dummyDate = new Date();
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const adjustedDummyDate = new Date(dummyDate.toLocaleString('en-US', {
        timeZone: userTz,
    }));
    const otherTimezoneDate = new Date(dummyDate.toLocaleString('en-US', {
        timeZone: ianaTimeZone,
    }));
    // Why do we use adjustedDummyDate rather than dummyDate? The reason is that
    // the toLocaleString passed to new Date effectively rounds the date to the
    // nearest second because the localeString is of format "8/5/2021, 11:16:49
    // AM". So, the differenceInMs will be off by a few MS if both dates don't get
    // transformed in the same manner. I.E., the diff (between US central and HK)
    // will be something like -46799290 or -46799555, rather than -46800000 (13*
    // 60 * 60 * 1000 = 46800000). This makes the time difference be 12.99 hours
    // rather than 13 which is both wrong and messes up the logic that comes next
    const differenceInMs = adjustedDummyDate.valueOf() - otherTimezoneDate.valueOf();
    return differenceInMs;
};
export const startOfDayTz = (date, targetTimezone) => {
    const dateTime = typeof date === 'number' ? new Date(date) : date;
    return DateTime.fromJSDate(dateTime)
        .setZone(targetTimezone)
        .startOf('day')
        .toJSDate();
};
export const endOfDayTz = (date, targetTimezone) => {
    const dateTime = typeof date === 'number' ? new Date(date) : date;
    return DateTime.fromJSDate(dateTime)
        .setZone(targetTimezone)
        .endOf('day')
        .toJSDate();
};
export const deriveDateValue = (isoDate) => {
    return parseISO(isoDate).valueOf();
};
/**
 * These util functions are divided into the following categories:
 *    - Formatting
 *    - Timestamp
 *    - Time Period
 *    - Edit Date
 */
/** Formatting Functions Start */
export function formatApiDate(date, timezone) {
    return genericDateFormat(date, IsoDateFormat, timezone);
}
export const formatEpochDate = (epochDateNumber, fullDateTimeFormat) => {
    var date = new Date(0);
    date.setUTCSeconds(epochDateNumber);
    if (date.toString() === 'Invalid Date')
        return global.NOT_AVAILABLE;
    return genericDateFormat(date, fullDateTimeFormat);
};
export const getDateString = (params) => {
    const { value, isTimeFrom, ianaTimeZoneCode, format } = params;
    if (value === '' || value === null || value === undefined) {
        return '';
    }
    let newDate;
    if (value instanceof Date) {
        newDate = value;
    }
    else {
        newDate = parseISO(value);
    }
    if (isTimeFrom) {
        return formatDistance(newDate, new Date(), { addSuffix: true });
    }
    else {
        const processedDate = ianaTimeZoneCode
            ? convertToSiteTz(newDate, ianaTimeZoneCode)
            : newDate;
        return genericDateFormat(processedDate, format, ianaTimeZoneCode);
    }
};
export const deriveDateString = (value, locale, isTimeFrom, ianaTimeZoneCode) => {
    return getDateString({
        value,
        isTimeFrom,
        ianaTimeZoneCode,
        format: locale.dateTimeFormat,
    });
};
export const getMidday = (date) => {
    return setHours(setMinutes(setSeconds(setMilliseconds(date, 0), 0), 0), 12);
};
const selectPartsForDisplay = (parts) => {
    if (parts.years) {
        return parts.months ? `${parts.years} ${parts.months}` : parts.years;
    }
    else if (parts.months) {
        return parts.days ? `${parts.months} ${parts.days}` : parts.months;
    }
    else if (parts.days) {
        return parts.hours ? `${parts.days} ${parts.hours}` : parts.days;
    }
    else if (parts.hours) {
        return parts.minutes ? `${parts.hours} ${parts.minutes}` : parts.hours;
    }
    else if (parts.minutes) {
        return parts.seconds ? `${parts.minutes} ${parts.seconds}` : parts.minutes;
    }
    else if (parts.seconds) {
        return parts.seconds;
    }
};
export const formatDistanceMixedUnits = (startDate, endDate, abbreviateUnits) => {
    const now = new Date();
    const parts = {};
    const diffInYears = Math.abs(differenceInYears(startDate, endDate));
    if (diffInYears > 0) {
        parts.years = formatDistanceStrict(now, addYears(now, diffInYears), {
            unit: 'year',
        });
        if (abbreviateUnits)
            parts.years = parts.years.replace(new RegExp(/\syear(s)?/), 'y');
    }
    const diffInMonths = Math.abs(differenceInMonths(startDate, subYears(endDate, diffInYears)));
    if (diffInMonths > 0) {
        parts.months = formatDistanceStrict(now, addMonths(now, diffInMonths), {
            unit: 'month',
        });
        if (abbreviateUnits)
            parts.months = parts.months.replace(new RegExp(/\smonth(s)?/), 'mo');
    }
    const diffInDays = Math.abs(differenceInDays(startDate, subMonths(endDate, diffInMonths)));
    if (diffInDays > 0) {
        parts.days = formatDistanceStrict(now, addDays(now, diffInDays), {
            unit: 'day',
        });
        if (abbreviateUnits)
            parts.days = parts.days.replace(new RegExp(/\sday(s)?/), 'd');
    }
    const diffInHours = Math.abs(differenceInHours(startDate, subDays(endDate, diffInDays)));
    if (diffInHours > 0) {
        parts.hours = formatDistanceStrict(now, addHours(now, diffInHours), {
            unit: 'hour',
        });
        if (abbreviateUnits)
            parts.hours = parts.hours.replace(new RegExp(/\shour(s)?/), 'h');
    }
    const diffInMinutes = Math.abs(differenceInMinutes(startDate, subHours(endDate, diffInHours)));
    if (diffInMinutes > 0) {
        parts.minutes = formatDistanceStrict(now, addMinutes(now, diffInMinutes), {
            unit: 'minute',
        });
        if (abbreviateUnits)
            parts.minutes = parts.minutes.replace(new RegExp(/\sminute(s)?/), 'm');
    }
    const diffInSeconds = Math.abs(differenceInSeconds(startDate, subMinutes(endDate, diffInMinutes)));
    if (diffInSeconds > 0) {
        parts.seconds = formatDistanceStrict(now, addSeconds(now, diffInSeconds), {
            unit: 'second',
        });
        if (abbreviateUnits)
            parts.seconds = parts.seconds.replace(new RegExp(/\ssecond(s)?/), 's');
    }
    return selectPartsForDisplay(parts);
};
export const formatMinutesMixedUnits = (numMinutes) => {
    if (numMinutes > 0) {
        const now = new Date();
        const nowPlusNumMinutes = addMinutes(now, numMinutes);
        return formatDistanceMixedUnits(now, nowPlusNumMinutes, true);
    }
    else {
        return '0 min';
    }
};
export const deriveDurationString = (durationInMilliseconds) => {
    if (durationInMilliseconds === 0)
        return global.NOT_AVAILABLE;
    const baseDate = new Date(0);
    return (formatDistanceMixedUnits(baseDate, addMilliseconds(baseDate, durationInMilliseconds), true) || global.NOT_AVAILABLE);
};
// Given date, return the start of local date
// as UTC-0 as ISO timestamp without timezone
export function startOfDayUTC(date) {
    const utcDate = getUTCDate(startOfDay(date));
    return asISO(utcDate);
}
// Given date, return the end of local date
// as UTC-0 as ISO timestamp without timezone
export function endOfDayUTC(date) {
    const utcDate = getUTCDate(endOfDay(date));
    return asISO(utcDate);
}
export function asISO(date) {
    return genericDateFormat(new Date(date), "yyyy-MM-dd'T'HH:mm:ss");
}
export function asIsoWithTz(date) {
    const offset = date.getTimezoneOffset();
    const datePlusOffset = addMinutes(date, offset);
    return asISO(datePlusOffset);
}
export function titleForTimeRange(timeRange, locale) {
    if (timeRange.fromDate === timeRange.toDate) {
        return deriveDateString(timeRange.fromDate, locale, false);
    }
    return `${deriveDateString(timeRange.fromDate, locale, false)} - ${deriveDateString(timeRange.toDate, locale, false)}`;
}
/** Formatting Functions End */
/** Timestamp Functions Start */
export function getUTCDate(dirtyDate) {
    return new Date(dirtyDate.getUTCFullYear(), dirtyDate.getUTCMonth(), dirtyDate.getUTCDate(), dirtyDate.getUTCHours(), dirtyDate.getUTCMinutes(), dirtyDate.getUTCSeconds());
}
export const getUnixTimestamp = (date) => {
    const newDate = typeof date === 'string' ? parseISO(date) : date;
    return setMilliseconds(newDate, 0).getTime() / 1000;
};
export const addDaysAndDeriveDateValue = (isoDate, days) => {
    const date = addDays(parseISO(isoDate), days);
    return date.valueOf();
};
export const dateStringToValue = (dateString, addOneDay) => {
    let isoDate;
    // if includes(' ') that means the date is of format '2019-09-03 11:45'
    // otherwise of format '2019-09-03'
    if (dateString.includes(' ')) {
        const split = dateString.split(' ');
        isoDate = `${split[0]}T${split[1]}:00`;
    }
    else {
        isoDate = `${dateString}T00:00:00`;
    }
    if (addOneDay) {
        return addDaysAndDeriveDateValue(isoDate, 1);
    }
    return deriveDateValue(isoDate);
};
/** Timestamp Functions End */
/** Time Period Functions Start */
export const roundUpTo = (roundTo) => (x) => Math.ceil(x / roundTo) * roundTo;
export const roundUpTo15Minutes = roundUpTo(1000 * 60 * 15);
export const getDateLast15MinuteStep = (timeToRoundDown = new Date()) => {
    const roundedTime = roundToNearestMinutes(timeToRoundDown, { nearestTo: 15 });
    // So that our time isn't in the future
    const ensureRoundDown = timeToRoundDown > roundedTime ? roundedTime : subMinutes(roundedTime, 15);
    return ensureRoundDown;
};
export function generateEpochFromDate(date) {
    return setMilliseconds(date, 0).getTime() / 1000;
}
export function generateToAndFromEpoch(timePeriod) {
    const fifteenMinutesFromNow = new Date(roundUpTo15Minutes(new Date())).getTime() / 1000;
    let from, to;
    // setMs to 0 to avoid getting decimal numbers
    from = generateEpochFromDate(timePeriod.fromDate);
    to = generateEpochFromDate(timePeriod.toDate);
    if (to > fifteenMinutesFromNow)
        to = fifteenMinutesFromNow;
    return {
        to,
        from,
    };
}
export const getTimeStampsForPreviousPeriod = (fromTimeStamp, toTimeStamp) => {
    const timePeriodLength = toTimeStamp - fromTimeStamp;
    const previousFromTimeStamp = fromTimeStamp - timePeriodLength;
    const previousToTimeStamp = toTimeStamp - timePeriodLength;
    return [previousFromTimeStamp, previousToTimeStamp];
};
const generateLastPeriodsFromTimeInterval = (timePeriod, numDays) => {
    const { fromDate, toDate } = timePeriod;
    const previousPeriods = [];
    for (let i = 3; i > 0; i--) {
        const period = {
            from: generateEpochFromDate(startOfDay(subDays(fromDate, i * numDays))),
            to: generateEpochFromDate(endOfDay(subDays(toDate, i * numDays))),
        };
        previousPeriods.push(period);
    }
    return previousPeriods;
};
export const generatePreviousPeriodsForComparison = (timePeriod) => {
    const { to, from } = generateToAndFromEpoch(timePeriod);
    let previousPeriods = [];
    if (timePeriod.title === 'Today' || timePeriod.title === 'Yesterday') {
        previousPeriods = generateLastPeriodsFromTimeInterval(timePeriod, 1);
    }
    else if (timePeriod.title === 'Last 7 days') {
        previousPeriods = generateLastPeriodsFromTimeInterval(timePeriod, 7);
    }
    else if (timePeriod.title === 'Last 30 days') {
        previousPeriods = generateLastPeriodsFromTimeInterval(timePeriod, 30);
    }
    else if (timePeriod.title === 'Last month' ||
        timePeriod.title === 'This month') {
        previousPeriods = getPreviousMonthsEpoch(from, 3);
    }
    else {
        // If custom then just take the time period and take exactly the previous 3
        // periods of this length
        const timePeriodLength = to - from + 1;
        for (let i = 3; i > 0; i--) {
            const period = {
                to: to - timePeriodLength * i,
                from: from - timePeriodLength * i,
            };
            previousPeriods.push(period);
        }
    }
    const currentPeriod = {
        to,
        from,
    };
    return {
        previousPeriods,
        currentPeriod,
    };
};
export function getPreviousMonthsEpoch(startOfMonthEpoch, howMany) {
    let previousMonths = [];
    let currentEndMonth = startOfMonthEpoch;
    for (let i = 0; i < howMany; i++) {
        const endOfLastMonth = endOfDay(subDays(currentEndMonth * 1000, 1));
        const startOfLastMonth = new Date(endOfLastMonth.getFullYear(), endOfLastMonth.getMonth());
        const from = generateEpochFromDate(startOfLastMonth);
        const to = generateEpochFromDate(endOfLastMonth);
        previousMonths.unshift({ from, to });
        currentEndMonth = from;
    }
    return previousMonths;
}
/** Time Period Functions End */
/** Edit Date Functions Start */
export const stripTime = (dirtyDate) => {
    const newDate = new Date(dirtyDate);
    newDate.setHours(0);
    newDate.setMinutes(0);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    return newDate;
};
export const setMidnight = (dirtyDate) => {
    const newDate = new Date(dirtyDate);
    newDate.setHours(23);
    newDate.setMinutes(59);
    newDate.setSeconds(59);
    return newDate;
};
/** Edit Date Functions End */
export const formatDateForNotificationsAPI = (date, timeZone) => {
    const dateFormat = 'yyyy-MM-dd';
    if (timeZone)
        return formatIanaTimeZone(date, dateFormat, timeZone);
    else
        return genericDateFormat(date, dateFormat);
};
export const getYesterdayInStringFormat = () => {
    return genericDateFormat(subDays(new Date(), 1), 'yyyy-MM-dd');
};
export const generateLocalDatesBetween = (start, end) => {
    const res = [];
    // make sure we don't mutate the original start date;
    const s = new Date(start);
    for (let dt = s; dt <= end; dt.setDate(dt.getDate() + 1)) {
        // again new date to ensure we're not mutating the same ref
        res.push(new Date(dt));
    }
    return res.map((date) => genericDateFormat(date, 'yyyy-MM-dd'));
};
export const formatNotifApiDate = (date, withoutTime) => {
    if (withoutTime) {
        return genericDateFormat(date, 'yyyy-MM-dd');
    }
    return asIsoWithTz(date);
};
export const getHumanReadableDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds - hours * 3600) / 60);
    return `${hours ? `${hours} hrs ` : ''}${mins || hours ? `${mins} mins` : ''}`;
};
export const convertToLocalTz = (date) => {
    return DateTime.fromJSDate(date).toLocal().toJSDate();
};
export const convertToSiteTz = (date, siteTimezone, options) => {
    return DateTime.fromJSDate(date)
        .setZone(siteTimezone, { keepLocalTime: options === null || options === void 0 ? void 0 : options.keepLocalTime })
        .toJSDate();
};
// this will need to be fixed with the function above
// but to not break anything for now we are doing this
export const _convertToSiteTz = (date, siteTimezone, options) => {
    if (!siteTimezone)
        return date;
    const luxonDate = DateTime.fromJSDate(date).setZone(siteTimezone, {
        keepLocalTime: options === null || options === void 0 ? void 0 : options.keepLocalTime,
    });
    const utcDate = new Date(luxonDate.year, luxonDate.month - 1, luxonDate.day, luxonDate.hour, luxonDate.minute, luxonDate.second, luxonDate.millisecond);
    return utcDate;
};
/**
 * @param timezone defaults to local timezone if undefined
 *
 * returns ISO 8601 string
 */
export const getCurrentTime = (timezone) => {
    return DateTime.fromJSDate(new Date()).setZone(timezone).toISO();
};
/**
 * @param dateTime ISO 8601 string
 * @param timezone defaults to local timezone if undefined
 *
 * returns ISO 8601 string
 */
export const getStartOfDay = (dateTime, timezone) => {
    return DateTime.fromISO(dateTime, { zone: timezone }).startOf('day').toISO();
};
/**
 * @param dateTime ISO 8601 string
 * @param timezone defaults to local timezone if undefined
 *
 * returns ISO 8601 string
 */
export const getEndOfDay = (dateTime, timezone) => {
    return DateTime.fromISO(dateTime, { zone: timezone }).endOf('day').toISO();
};
export const getStartOfMonth = (date, timezone) => {
    const dateTime = typeof date === 'number' ? new Date(date) : date;
    return DateTime.fromJSDate(dateTime)
        .setZone(timezone)
        .startOf('month')
        .toJSDate();
};
export const getEndOfMonth = (date, timezone) => {
    const dateTime = typeof date === 'number' ? new Date(date) : date;
    return DateTime.fromJSDate(dateTime)
        .setZone(timezone)
        .endOf('month')
        .toJSDate();
};
/**
 * @param dateTime ISO 8601 string
 */
export const isFirstDayOfMonth = (dateTime) => {
    return DateTime.fromISO(dateTime).day === 1;
};
/**
 * @param dateTime ISO 8601 string
 * @param timezone defaults to local timezone if undefined
 */
export const isSameDay = (dateTime1, dateTime2, timezone) => {
    return DateTime.fromISO(dateTime1)
        .setZone(timezone)
        .hasSame(DateTime.fromISO(dateTime2).setZone(timezone), 'day');
};
/**
 * @param dateTime ISO 8601 string
 * @param timezone defaults to local timezone if undefined
 */
export const isToday = (dateTime, timezone) => {
    return (DateTime.fromISO(dateTime).setZone(timezone).toISODate() ===
        DateTime.now().setZone(timezone).toISODate());
};
/**
 * @param date JS Date object
 * @param timezone defaults to local timezone if undefined
 */
export const formatToIsoDate = (date, timezone) => {
    return DateTime.fromJSDate(date).setZone(timezone).toISODate();
};
/**
 * @param dateTime ISO 8601 string
 * @param start ISO 8601 string
 * @param end ISO 8601 string
 * @param timezone defaults to local timezone if undefined
 */
export const isWithinInterval = (dateTime, intervalParams, timezone) => {
    const { start, end } = intervalParams;
    const dateToCheck = DateTime.fromISO(dateTime).setZone(timezone);
    const interval = Interval.fromDateTimes(DateTime.fromISO(start).setZone(timezone), DateTime.fromISO(end).setZone(timezone));
    return interval.contains(dateToCheck);
};
/**
 * @param dateTime ISO 8601 string
 *
 * returns JS Date object
 */
export const convertIsoToJSDate = (dateTime) => {
    return DateTime.fromISO(dateTime).toJSDate();
};
/**
 * @param dateTime ISO 8601 string
 *
 * returns unix timestamp in milliseconds
 */
export const convertIsoToMillis = (dateTime) => {
    return DateTime.fromISO(dateTime).toMillis();
};
/**
 * @param dateTimeStart ISO 8601 string
 * @param dateTimeEnd ISO 8601 string
 *
 * returns number (integer)
 */
export const getDifferenceInDays = (dateTimeStart, dateTimeEnd) => {
    const dayDifference = DateTime.fromISO(dateTimeEnd)
        .diff(DateTime.fromISO(dateTimeStart), 'days')
        .toObject().days;
    if (!isDefined(dayDifference)) {
        return 0;
    }
    return Math.ceil(Math.abs(dayDifference)) + 1;
};
